import type { IScope } from "angular";
import { cloneDeep, isEmpty } from "lodash";
import { finalize } from "rxjs/operators";

import type { ICoverageCalculationsService } from "App/Modules/Gui";
import type {
    AnonymizationLevel,
    IEquipmentFunctionalityType,
    IResponseWithLoading,
    IrMap,
    ISiteEquipmentWithCoverages,
    IVideoStream,
    ResolutionMode,
} from "App/Modules/Models";
import { AnonymizationLevelFunctions, FpsFunctions, ResolutionModeFunctions } from "App/Modules/Models";
import type { IEquipmentTypeRepository, ISiteVideoStreamsRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve, IImageService } from "App/Modules/Services";
import { PatchDelta } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";
import type { IConfig } from "App/Modules/Utils";

export default class EditVideoStreamComponentController {
    public static $inject: string[] = [
        "$scope",
        "$translate",
        "mmSiteVideoStreamsRepository",
        "irConfig",
        "mmImageService",
        "Upload",
        "mmCoverageCalculationsService",
        "mmEquipmentTypeRepository",
    ];

    public siteId: string;

    public siteEquipment: ISiteEquipmentWithCoverages;

    public supportedVideoStreamProperties: IEquipmentFunctionalityType;

    public anonymizationLevels: IrMap<AnonymizationLevel, string>;

    public resolutionModes: IrMap<ResolutionMode, string>;

    public fpsOptions: IrMap<number, string>;

    public isUploadingImage: boolean;

    public original: IVideoStream;

    public modified: IVideoStream;

    public currentResolution: string;

    public isInUse: boolean;

    public tooltip: string;

    public resolve: IFormContainerResolve<[IVideoStream, boolean], ISiteEquipmentWithCoverages, IVideoStream>;

    public deleteResponse: IResponseWithLoading<void>;

    public editResponse: IResponseWithLoading<IVideoStream>;

    public isEditable: boolean;

    constructor(
        private readonly $scope: IScope,
        private readonly $translate: angular.translate.ITranslateService,
        private readonly videoStreamsRepository: ISiteVideoStreamsRepository,
        private readonly config: IConfig,
        private readonly imageService: IImageService,
        private readonly Upload: any,
        private readonly coverageCalculationsService: ICoverageCalculationsService,
        private readonly equipmentTypeRepository: IEquipmentTypeRepository,
    ) {}

    public $onInit(): void {
        this.siteEquipment = this.resolve.bindings;
        this.siteId = this.siteEquipment.siteFriendlyId;
        const [original, isEditable] = this.resolve.formData;
        this.original = original;
        this.isEditable = isEditable;
        this.modified = cloneDeep(this.original);
        this.currentResolution = this.getCurrentResolutionString();
        this.canBeDeleted();
        this.tooltip = this.$translate.instant("Gui.Manage.RemoveVideoStreamToolTip");

        extendWithLoadingAndFail(
            this.equipmentTypeRepository.getSupportedVideoStreamProperties(this.siteEquipment.type.id),
        )
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    if (response.data) {
                        this.resolutionModes = ResolutionModeFunctions.GetSupportedResolutionModesDictionary(
                            response.data.supportedResolutionModes,
                        );

                        this.fpsOptions = response.data.supportedFpses.map((fps) => ({
                            key: fps,
                            value: FpsFunctions.GetFpsString(fps),
                        }));

                        this.supportedVideoStreamProperties = response.data;
                    }
                },
                (error) => {
                    this.supportedVideoStreamProperties = error;
                },
            );

        this.anonymizationLevels = AnonymizationLevelFunctions.GetAnonymizationLevelDictionary();

        this.convertToAbsoluteCoordinates();
    }

    public cancel(): void {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public deleteVideoStream(): void {
        extendWithLoadingAndFail(this.videoStreamsRepository.deleteVideoStream(this.siteId, this.modified.id))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response: IResponseWithLoading<void>) => {
                    this.deleteResponse = response;
                    if (!response.isLoading) {
                        this.resolve.options.onOk(null);
                    }
                },
                (error) => {
                    this.deleteResponse = error;
                },
            );
    }

    public uploadPlanningImage(file: any): void {
        if (file) {
            const uploadUrl = `${this.config.apiUrl}sites/${this.siteId}/videoStreams/${this.original.id}/planningImage`;

            const imageUrl = `${this.config.apiUrl}sites/${this.siteId}/videoStreams/${this.original.id}/images/latest`;

            this.isUploadingImage = true;

            file.upload = this.Upload.upload({
                data: { file },
                method: "PUT",
                url: uploadUrl,
            });

            file.upload.then(
                () => {
                    return this.imageService.forceImageReload(imageUrl).then((result: any) => {
                        this.isUploadingImage = false;
                        this.resolve.options.onOk(this.original);
                        return result;
                    });
                },
                (error: any) => {
                    this.isUploadingImage = false;
                    if (error.status > 0) {
                        throw new Error("Upload failed");
                    }
                },
                (evt: any) => {
                    file.progress = Math.min(100, (100 * evt.loaded) / evt.total);
                },
            );
        }
    }

    public editVideoStream() {
        this.convertToRelativeCoordinates();

        if (this.resolutionChanged()) {
            const newResolution = this.currentResolution.split("x");
            this.modified.resolution.resolutionX = parseInt(newResolution[0], 10);
            this.modified.resolution.resolutionY = parseInt(newResolution[1], 10);
        }

        const delta = PatchDelta.difference(this.original, this.modified);

        if (!isEmpty(delta)) {
            extendWithLoadingAndFail(this.videoStreamsRepository.patchVideoStream(this.siteId, this.original.id, delta))
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (response) => {
                        this.editResponse = response;
                        if (!response.isLoading && response.data) {
                            this.resolve.options.onOk(response.data);
                        }
                    },
                    (error) => {
                        this.editResponse = error;
                    },
                );
        } else {
            this.cancel();
        }
    }

    public canBeDeleted(): void {
        extendWithLoadingAndFail(this.videoStreamsRepository.isVideoStreamInUse(this.siteId, this.original.id))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe((response) => {
                this.isInUse = response.data;
            });
    }

    private convertToRelativeCoordinates() {
        this.modified.blackMaskPolygons.forEach((polygon) => {
            this.coverageCalculationsService.polygonToNormalized(polygon, this.modified.images.latest);
        });
    }

    private convertToAbsoluteCoordinates() {
        this.modified.blackMaskPolygons.forEach((polygon) => {
            this.coverageCalculationsService.polygonToAbsolute(polygon, this.modified.images.latest);
        });
    }

    private getCurrentResolutionString(): string {
        return `${this.original.resolution.resolutionX}x${this.original.resolution.resolutionY}`;
    }

    private resolutionChanged(): boolean {
        return (
            this.currentResolution !== `${this.original.resolution.resolutionX}x${this.original.resolution.resolutionY}`
        );
    }
}
